"use client"

import {startTransition, useEffect, useMemo, useRef, useState} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';

import styles from './styles/Header.module.scss';

import NavigationPrimary from './NavigationPrimary';
import NavigationSecondary from './NavigationSecondary';
import dynamic from "next/dynamic";
import {primaryNavHeight} from "../../utils/Settings";
import {useStores} from "../../stores/useStores";
import {useShouldDisplayHero} from "../../hooks/useShouldDisplayHero";
import {useTranslateClassName} from "../../hooks/useTranslateClassName";

const HeroContainer = dynamic(() => import("./HeroContainer"), {loading: () => <></>});

const Header = observer((props) => {
    const {interfaceStore, organizationStore} = useStores();
    const displayHero = useShouldDisplayHero();
    const translateClassName = useTranslateClassName();

    const {displayViewHomepageButton = false, allowEditing = false, isWelcomePage = false} = props;

    const alwaysShowHeader = organizationStore.currentOrganization.json_data.settings?.appearance?.alwaysShowHeader;
    const [visible, setVisible] = useState((!props.heroIsEnabled || alwaysShowHeader) && !isWelcomePage);
    const secondaryNavRef = useRef(null);

    useEffect(() => {
        // this is running in a useEffect because scrollPosition is always 0 on SSR so otherwise a reload while scrolled
        // down doesn't hydrate with the correct classNames and it doesn't update
        startTransition(() => {
            if (alwaysShowHeader || interfaceStore.scrollPosition > primaryNavHeight || !displayHero) {
                setVisible(true);
            } else if (visible) {
                setVisible(false);
            }
        })
    }, [interfaceStore.scrollPosition])

    const secondaryNavHeight = useMemo(() => {
        if (secondaryNavRef?.current) {
            return secondaryNavRef.current.offsetHeight;
        }
        return 0;
    },[secondaryNavRef?.current])

    return (
        <>
            <ul id={"skiptocontent"} className={`${styles.skip} ${translateClassName}`}>
                <li key={'navigation'}>
                    <a href={(interfaceStore.breakpoint === "break-point-lg") ? "#navigation" : "#MobileNavigationMenu"} tabIndex="2">Skip to Navigation</a>
                </li>
            </ul>
            <NavigationPrimary
                isWelcomePage={isWelcomePage}
                heroIsEnabled={displayHero}
                visible={visible}
            />
            {displayHero &&
                <HeroContainer
                    displayViewHomepageButton={displayViewHomepageButton}
                    allowEditing={allowEditing}
                    isWelcomePage={isWelcomePage}
                    secondaryNavHeight={secondaryNavHeight}
                />}
            <span ref={secondaryNavRef}>
                <NavigationSecondary
                    isWelcomePage={isWelcomePage}
                    visible={visible}
                    heroIsEnabled={displayHero}
                />
            </span>
        </>
    );
});

Header.propTypes = {
    displayHero: PropTypes.bool.isRequired, // displayHero is prop passed from config because not all pages have hero enabled
    displayViewHomepageButton: PropTypes.bool,
    allowEditing: PropTypes.bool,
    isWelcomePage: PropTypes.bool,
    handleViewHomepage: PropTypes.func,
};

export default Header;
